import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getGamificationStats, getLeaderboard } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrustMirrorCard } from '../components/TrustMirrorCard';
import { toast } from 'sonner';
import {
    Trophy,
    Star,
    Zap,
    Medal,
    Crown,
    Flame,
    Target,
    Heart,
    Dumbbell,
    Moon,
    Apple,
    Plane
} from 'lucide-react';

const achievements = [
    { id: 'first_meal', name: 'Erste Mahlzeit', description: 'Logge deine erste Mahlzeit', icon: Apple, color: 'bg-lime-100 dark:bg-lime-900/50', iconColor: 'text-lime-600', points: 10 },
    { id: 'water_hero', name: 'Wasser-Held', description: 'Trinke 2.5L an einem Tag', icon: Heart, color: 'bg-blue-100 dark:bg-blue-900/50', iconColor: 'text-blue-600', points: 20 },
    { id: 'first_workout', name: 'Erstes Workout', description: 'Absolviere dein erstes Training', icon: Dumbbell, color: 'bg-orange-100 dark:bg-orange-900/50', iconColor: 'text-orange-600', points: 15 },
    { id: 'sleep_master', name: 'Schlafmeister', description: '7 Tage optimalen Schlaf', icon: Moon, color: 'bg-indigo-100 dark:bg-indigo-900/50', iconColor: 'text-indigo-600', points: 50 },
    { id: 'streak_7', name: '7-Tage-Streak', description: '7 Tage am Stück aktiv', icon: Flame, color: 'bg-red-100 dark:bg-red-900/50', iconColor: 'text-red-600', points: 70 },
    { id: 'streak_30', name: '30-Tage-Streak', description: '30 Tage am Stück aktiv', icon: Crown, color: 'bg-yellow-100 dark:bg-yellow-900/50', iconColor: 'text-yellow-600', points: 300 },
    { id: 'traveler', name: 'Weltreisender', description: 'Plane deine erste Reise', icon: Plane, color: 'bg-sky-100 dark:bg-sky-900/50', iconColor: 'text-sky-600', points: 25 },
    { id: 'balanced', name: 'Ausgewogen', description: 'Erreiche alle Tagesziele', icon: Target, color: 'bg-purple-100 dark:bg-purple-900/50', iconColor: 'text-purple-600', points: 100 },
];

export default function Achievements() {
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [statsRes, leaderboardRes] = await Promise.all([
                getGamificationStats(),
                getLeaderboard()
            ]);
            setStats(statsRes.data);
            setLeaderboard(leaderboardRes.data?.leaderboard || leaderboardRes.data || []);
        } catch (error) {
            console.error('Failed to load achievements:', error);
            toast.error('Fehler beim Laden der Erfolge');
        } finally {
            setLoading(false);
        }
    };

    const getLevelProgress = () => {
        if (!stats) return 0;
        const pointsInLevel = stats.total_points % 100;
        return pointsInLevel;
    };

    const pointsToNextLevel = () => {
        if (!stats) return 100;
        return 100 - (stats.total_points % 100);
    };

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="achievements-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-6"
            >
                {/* Header */}
                <div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold flex items-center gap-3">
                        <Trophy className="w-8 h-8 text-yellow-500" />
                        Erfolge
                    </h1>
                    <p className="text-muted-foreground mt-1">Dein Fortschritt und deine Erfolge</p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/30 dark:to-orange-950/30 border-yellow-200 dark:border-yellow-800">
                        <CardContent className="p-4 text-center">
                            <div className="w-12 h-12 mx-auto rounded-full bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center mb-2">
                                <Star className="w-6 h-6 text-yellow-600" />
                            </div>
                            <p className="text-3xl font-bold">{stats?.total_points || 0}</p>
                            <p className="text-xs text-muted-foreground">Punkte</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200 dark:border-purple-800">
                        <CardContent className="p-4 text-center">
                            <div className="w-12 h-12 mx-auto rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-2">
                                <Crown className="w-6 h-6 text-purple-600" />
                            </div>
                            <p className="text-3xl font-bold">{stats?.level || 1}</p>
                            <p className="text-xs text-muted-foreground">Level</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border-red-200 dark:border-red-800">
                        <CardContent className="p-4 text-center">
                            <div className="w-12 h-12 mx-auto rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mb-2">
                                <Flame className="w-6 h-6 text-red-600" />
                            </div>
                            <p className="text-3xl font-bold">{stats?.current_streak || 0}</p>
                            <p className="text-xs text-muted-foreground">Streak</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
                        <CardContent className="p-4 text-center">
                            <div className="w-12 h-12 mx-auto rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center mb-2">
                                <Zap className="w-6 h-6 text-green-600" />
                            </div>
                            <p className="text-3xl font-bold">{stats?.longest_streak || 0}</p>
                            <p className="text-xs text-muted-foreground">Bester Streak</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Trust Mirror — shows how Nanouk learns from your insight reads */}
                <TrustMirrorCard />

                {/* Level Progress */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="level-badge w-14 h-14 rounded-2xl flex items-center justify-center">
                                    <span className="text-foreground font-bold text-xl">{stats?.level || 1}</span>
                                </div>
                                <div>
                                    <p className="font-semibold">Level {stats?.level || 1}</p>
                                    <p className="text-sm text-muted-foreground">
                                        Noch {pointsToNextLevel()} Punkte bis Level {(stats?.level || 1) + 1}
                                    </p>
                                </div>
                            </div>
                            <Badge variant="outline" className="gap-1">
                                <Star className="w-3 h-3" />
                                {stats?.total_points || 0} Pkt
                            </Badge>
                        </div>
                        <Progress value={getLevelProgress()} className="h-3" />
                    </CardContent>
                </Card>

                <Tabs defaultValue="achievements">
                    <TabsList className="mb-4">
                        <TabsTrigger value="achievements">Erfolge</TabsTrigger>
                        <TabsTrigger value="leaderboard">Rangliste</TabsTrigger>
                    </TabsList>

                    <TabsContent value="achievements">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Medal className="w-5 h-5" />
                                    Alle Erfolge
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {achievements.map((achievement) => {
                                        const isUnlocked = stats?.badges?.some(b => b.id === achievement.id);
                                        return (
                                            <motion.div
                                                key={achievement.id}
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                className={`flex items-center gap-4 p-4 rounded-xl border ${
                                                    isUnlocked ? achievement.color : 'bg-secondary/30 opacity-60'
                                                }`}
                                            >
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                                    isUnlocked ? achievement.color : 'bg-secondary'
                                                }`}>
                                                    <achievement.icon className={`w-6 h-6 ${
                                                        isUnlocked ? achievement.iconColor : 'text-muted-foreground'
                                                    }`} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium">{achievement.name}</p>
                                                        {isUnlocked && (
                                                            <Badge className="bg-green-500 text-xs">Freigeschaltet</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">{achievement.description}</p>
                                                </div>
                                                <Badge variant="outline" className="gap-1">
                                                    <Star className="w-3 h-3" />
                                                    {achievement.points}
                                                </Badge>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="leaderboard">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Trophy className="w-5 h-5" />
                                    Top 10 Rangliste
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {leaderboard.length === 0 ? (
                                    <p className="text-center text-muted-foreground py-8">
                                        Noch keine Ranglisten-Einträge. Sei der Erste!
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {leaderboard.map((entry, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                                className={`flex items-center gap-4 p-4 rounded-xl ${
                                                    i === 0 ? 'bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/30 dark:to-orange-950/30 border border-yellow-200 dark:border-yellow-800' :
                                                    i === 1 ? 'bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-950/30 dark:to-slate-950/30 border' :
                                                    i === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border border-orange-200 dark:border-orange-800' :
                                                    'bg-secondary/50'
                                                }`}
                                            >
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                                    i === 0 ? 'bg-yellow-400 text-yellow-900' :
                                                    i === 1 ? 'bg-gray-300 text-gray-700' :
                                                    i === 2 ? 'bg-orange-400 text-orange-900' :
                                                    'bg-secondary text-muted-foreground'
                                                }`}>
                                                    {entry.rank}
                                                </div>
                                                <Avatar>
                                                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                                        {entry.name.slice(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1">
                                                    <p className="font-medium">{entry.name}</p>
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        <span>Level {entry.level}</span>
                                                        <span>•</span>
                                                        <span className="flex items-center gap-1">
                                                            <Flame className="w-3 h-3 text-orange-500" />
                                                            {entry.streak} Tage
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-lg">{entry.total_points}</p>
                                                    <p className="text-xs text-muted-foreground">Punkte</p>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </motion.div>
        </div>
    );
}
